// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * "Mål fra modellen" (`POST /survey/measure`, ReUseX schema v37) and the
 * per-part quantities it leaves behind: the report as Danish lines, and the
 * tag a part's quantity gets in Tabel › Dele (målt areal / målt volumen /
 * antal / indtastet). Pure: no React, no DOM.
 */

import type { GeomSource, SurveyMeasureReport, SurveyPart } from '../api/types';
import { formatNumber } from '../kortlaegning/vocab';

const n = (v: number | undefined) => v ?? 0;
const plural = (count: number, one: string, many: string) => `${formatNumber(count)} ${count === 1 ? one : many}`;

/** The report's lines, most telling first; empty counts are left out. */
export function measureLines(r: SurveyMeasureReport): string[] {
  const lines: string[] = [];
  const grounded = [
    n(r.wall_parts) > 0 ? plural(n(r.wall_parts), 'væg', 'vægge') : null,
    n(r.slab_parts) > 0 ? plural(n(r.slab_parts), 'dæk', 'dæk') : null,
    n(r.roof_parts) > 0 ? plural(n(r.roof_parts), 'tagflade', 'tagflader') : null,
    n(r.opening_parts) > 0 ? plural(n(r.opening_parts), 'åbning', 'åbninger') : null,
    n(r.count_parts) > 0 ? `${formatNumber(n(r.count_parts))} talt` : null,
  ].filter((x): x is string => x !== null);
  if (grounded.length > 0) lines.push(`Målt: ${grounded.join(', ')}`);
  if (n(r.parts_created) > 0) {
    lines.push(
      `${plural(n(r.parts_created), 'ny del', 'nye dele')} fra modellen${n(r.types_created) > 0 ? ` i ${plural(n(r.types_created), 'ny type', 'nye typer')}` : ''}`,
    );
  }
  if (n(r.quantities_seeded) > 0) lines.push(`${plural(n(r.quantities_seeded), 'mængde', 'mængder')} udfyldt`);
  if (n(r.buildups_suggested) > 0) lines.push(`${plural(n(r.buildups_suggested), 'opbygning', 'opbygninger')} foreslået`);
  if (n(r.walls_with_thickness) > 0) lines.push(`${plural(n(r.walls_with_thickness), 'væg', 'vægge')} med målt tykkelse`);
  if (n(r.parts_without_geometry) > 0) lines.push(`${plural(n(r.parts_without_geometry), 'del', 'dele')} uden geometri`);
  if (n(r.elements_missing) > 0) lines.push(`${plural(n(r.elements_missing), 'element', 'elementer')} findes ikke længere i modellen`);
  return lines;
}

/** The toast after "Mål fra modellen". */
export function measureMessage(r: SurveyMeasureReport): string {
  const lines = measureLines(r);
  if (lines.length === 0) return 'Intet at måle — opret vægge og dæk i modellen først';
  return lines.slice(0, 3).join(' · ');
}

/** Said when the server has no `POST /survey/measure` (before ReUseX schema v37). */
export const MEASURE_UNSUPPORTED = 'Serveren kan ikke måle fra modellen — det kræver en nyere ruxd';

const GEOM_LABEL: Record<GeomSource, string> = {
  wall_area: 'målt areal',
  opening_area: 'målt areal',
  slab_area: 'målt areal',
  plane_area: 'målt areal',
  solid_volume: 'målt volumen',
  count: 'antal',
};

/** How each source was measured, for the tag's title. */
const GEOM_TITLE: Record<GeomSource, string> = {
  wall_area: 'Vægflade i modellen: længde × højde − åbninger',
  opening_area: 'Åbningens areal i modellen',
  slab_area: 'Tagfladens areal i modellen',
  plane_area: 'Fladens areal i modellen',
  solid_volume: 'Dækkets volumen i modellen',
  count: 'Én genstand, talt',
};

export interface GeomTag {
  label: string;
  title: string;
  /** The quantity was typed over a measured one. */
  entered: boolean;
}

/** Within this, a typed quantity still equals the measured one. */
const SAME = 0.0005;

/**
 * The tag of a part's quantity: how the model measured it, `indtastet` (with
 * the measured value in the title) when someone typed over it, or null for a
 * part the model never measured (an older server, or no geometry).
 */
export function geomTag(
  part: Pick<SurveyPart, 'geom_source' | 'geom_quantity' | 'geom_unit' | 'quantity' | 'provenance'>,
  unit?: string,
): GeomTag | null {
  const src = part.geom_source;
  if (!src) return part.provenance?.quantity?.kind === 'entered' ? { label: 'indtastet', title: 'Mængden er tastet ind', entered: true } : null;
  const measured = part.geom_quantity ?? null;
  const sameUnit = unit === undefined || !part.geom_unit || part.geom_unit === unit;
  if (!sameUnit && measured !== null) {
    return {
      label: `målt ${formatNumber(measured, 2)} ${part.geom_unit}`,
      title: `Modellen målte i ${part.geom_unit}, typen tæller i ${unit} — mængden er ikke overført`,
      entered: false,
    };
  }
  const typed =
    part.provenance?.quantity?.kind === 'entered' || (measured !== null && Math.abs(measured - part.quantity) > SAME);
  if (typed && measured !== null) {
    return {
      label: 'indtastet',
      title: `Tastet ind — modellen målte ${formatNumber(measured, 2)}${part.geom_unit ? ` ${part.geom_unit}` : ''}`,
      entered: true,
    };
  }
  return { label: GEOM_LABEL[src], title: GEOM_TITLE[src], entered: false };
}

/** Tonnes as a number without the unit: two decimals under 1 t, one above (the inspectors' big figure). */
export function tonnesFigure(t: number): string {
  return formatNumber(t, t < 1 ? 2 : 1);
}

/** The part's tonnes as text, or null when the server sends none. */
export function partMassText(part: Pick<SurveyPart, 'mass_t'>): string | null {
  if (part.mass_t === undefined || part.mass_t === null) return null;
  return `${tonnesFigure(part.mass_t)} t`;
}

/** The server sends per-part mass/geometry (ReUseX schema v37): Tabel › Dele shows the Masse column. */
export function partsCarryMass(parts: readonly Pick<SurveyPart, 'mass_t'>[]): boolean {
  return parts.some((p) => p.mass_t !== undefined);
}
